import type { UpdateOrderStatusParams } from '@/entities/order/model'
import { Button, Radio, Stack, Text } from '@mantine/core'
import { useMemo, useState } from 'react'
import { useOrderStatusMutation } from '@/entities/order/model'
import { deriveStatusOptions, useStateConfigQuery } from '@/entities/state/model'
import { useOrderGroupMembers } from '@/features/order/lib'

interface OrderStatusUpdateProps {
  orderId: string
  currentStatus: string
  lastUpdateTime?: string
  onSuccess?: (newStatus: string) => void
}

// Status options are now derived from state configuration instead of hardcoded values

/**
 * Reusable component for updating order status
 * Can be embedded in different UI containers (modal, drawer, etc.)
 */
export function OrderStatusUpdate({
  orderId,
  currentStatus,
  lastUpdateTime,
  onSuccess,
}: OrderStatusUpdateProps) {
  const [selectedStatus, setSelectedStatus] = useState<string>(currentStatus)
  const isDirty = selectedStatus !== currentStatus

  const { data: stateConfig, isLoading: isLoadingStates } = useStateConfigQuery()
  const { mutateAsync: updateStatusMutation, isPending } = useOrderStatusMutation()
  const groupMemberOrderIds = useOrderGroupMembers(orderId)

  const statusOptions = useMemo(() => {
    if (!stateConfig?.states)
      return []
    return deriveStatusOptions(stateConfig.states)
  }, [stateConfig])

  /**
   * Handles the status update
   */
  async function handleStatusUpdate() {
    if (!selectedStatus || !orderId)
      return

    try {
      const updatePromises = groupMemberOrderIds.map((memberId) => {
        const updateParams: UpdateOrderStatusParams = {
          orderId: memberId,
          newStatus: selectedStatus,
          lastUpdateTime,
        }
        return updateStatusMutation(updateParams)
      })

      await Promise.all(updatePromises)

      if (onSuccess) {
        onSuccess(selectedStatus)
      }
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  return (
    <Stack>
      {isLoadingStates ? (
        <Text>Loading available statuses...</Text>
      ) : statusOptions.length === 0 ? (
        <Text>No status transitions available</Text>
      ) : (
        <Radio.Group
          id="order-status-update-radio-group"
          value={selectedStatus}
          onChange={setSelectedStatus}
          name="orderStatus"
        >
          {statusOptions.map(option => (
            <Radio
              key={option.value}
              value={option.value}
              label={option.label}
              size="md"
              color="gray.8"
              py="0.5rem"
            />
          ))}
        </Radio.Group>
      )}

      <Button
        variant="filled"
        color="gray.8"
        fullWidth
        size="md"
        radius="md"
        onClick={handleStatusUpdate}
        loading={isPending}
        disabled={!selectedStatus || isPending || statusOptions.length === 0}
      >
        {isDirty ? 'Update' : 'Done'}
      </Button>
    </Stack>
  )
}
